import { handleActions, createAction } from "redux-actions";
import {
  api,
  notification,
  checkEmptyObject,
  buildBaseUrl
} from "../../services";
import {
  authInterface,
  rootReducerInterface,
  UserI
} from "../../typings/interfaces";
import { PasswordInfoI, UserSettingsI } from "./authTypes";

const USER_PROFILE_GET = "USER_PROFILE_GET";
const USER_PROFILE_LOADING = "USER_PROFILE_LOADING";
const USER_PROFILE_UPDATE = "USER_PROFILE_UPDATE";
const USER_PASSWORD_UPDATE = "USER_PASSWORD_UPDATE";
const USER_SETTINGS_GET = "USER_SETTINGS_GET";
const USER_SETTINGS_UPDATE = "USER_SETTINGS_UPDATE";
const USER_PROFILE_AUTO_RENEWAL = "USER_PROFILE_AUTO_RENEWAL";
const USER_SET_PHONE_VERIFICATION_PENDING = "USER_SET_PHONE_VERIFICATION_PENDING";

const allowUnauthorizedError = async (response: any) => {
  if (response.status === 401) {
    return {};
  }

  await api.handleCommonError(response);
  throw response;
};

// Actions
export const setProfileLoading = createAction(USER_PROFILE_LOADING);

export const getUserProfile = createAction(
  USER_PROFILE_GET,
  async (user: UserI, callback: Function, shouldFetch = false) => {
    if (checkEmptyObject(user) && !shouldFetch) {
      callback && callback(user);

      return user;
    }

    try {
      const profile = await api.Auth.profile(allowUnauthorizedError);
      callback && callback(profile);

      return profile;
    } catch (error) {
      return null;
    }
  }
);

// Activate user auto renewal feature
export const autoRenewal = createAction(
  USER_PROFILE_AUTO_RENEWAL,
  async (autoRenewal: boolean, callback: Function) => {
    try {
      await api.Auth.autoRenewal(autoRenewal);

      notification.show(
        autoRenewal ? "Automatska obnova oglasa je aktivirana" : "Automatska obnova oglasa je deaktivirana"
      );

      callback && callback(true);

      return true;
    } catch (error) {
      callback && callback(false);

      return null;
    }
  }
);


export const updateUserProfile = createAction(
  USER_PROFILE_UPDATE,
  async (profileInfo: UserI, callback: Function) => {
    try {
      const skipNotification = profileInfo.skipNotification;

      if (profileInfo.userId) { // TODO: Davor - check if this is needed
        await api.User.updateProfile(profileInfo.userId, {
          ...profileInfo,
          isVerified: undefined,
          userId: undefined,
          skipNotification: undefined
        });
      } else {
        await api.Auth.updateProfile({
          ...profileInfo,
          isVerified: undefined,
          skipNotification: undefined
        });
      }

      if (!skipNotification) {
        notification.show(
          "Ažuriranje uspješno / Vaš je profil uspješno ažuriran"
        );
      }

      if (!profileInfo.isVerified) {
        await fetch(buildBaseUrl("/refresh-token"), { method: "POST" });
      }

      callback && callback(true);

      return true;
    } catch (error) {
      callback && callback(false);

      return null;
    }
  }
);

export const updatePassword = createAction(
  USER_PASSWORD_UPDATE,
  async (passwordInfo: PasswordInfoI, callback: Function) => {
    try {
      await api.Auth.updatePassword({ ...passwordInfo });

      notification.show(
        "Ažuriranje lozinke uspješno"
      );

      callback && callback(true);

      return true;
    } catch (error) {
      callback && callback(false);

      return null;
    }
  }
);

export const getUserSettings = createAction(
  USER_SETTINGS_GET,
  async (callback: Function) => {
    try {
      const settings = await api.Auth.getSettings();
      callback && callback(true);

      return settings;
    } catch (error) {
      callback && callback(false);

      return null;
    }
  }
);

export const updateUserSettings = createAction(
  USER_SETTINGS_UPDATE,
  async (settings: UserSettingsI, callback: Function) => {
    try {
      await api.Auth.updateSettings({ ...settings });

      notification.show(
        "Postavka ažuriranja uspješna"
      );

      callback && callback(true);

      return true;
    } catch (error) {
      callback && callback(false);

      return false;
    }
  }
);

export const setPhoneVerificationPending = createAction(
  USER_SET_PHONE_VERIFICATION_PENDING,
  (pending: boolean) => pending
);

// Selectors
export const selectLoadedUser = (state: rootReducerInterface) =>
  checkEmptyObject(state.auth.user);
export const selectUserInfo = (state: rootReducerInterface) => state.auth.user;
export const selectProfileLoading = (state: rootReducerInterface) => state.auth.profileLoading;

// Reducer
const initialState: authInterface = { user: null, profileLoading: true };
const setUserState = (state: authInterface, { payload }) => ({
  ...state,
  user: payload,
  profileLoading: false
});

export default handleActions(
  {
    [USER_PROFILE_GET]: setUserState,
    [USER_PROFILE_LOADING]: (state: authInterface, { payload }) => ({
      ...state,
      profileLoading: payload
    }),
    [USER_PROFILE_UPDATE]: (state: authInterface, { payload }) => ({
      ...state,
      user: {
        ...state.user,
        isVerified: payload,
        canUpdateUsername: payload ? false : state.user.canUpdateUsername
      }
    }),
    [USER_SETTINGS_GET]: (state: authInterface, { payload }) => ({
      ...state,
      settings: payload
    }),
    [USER_SET_PHONE_VERIFICATION_PENDING]: (state: authInterface, { payload }) => ({
      ...state,
      user: {
        ...state.user,
        phoneNumberVerificationPending: payload
      }
    })
  },
  initialState
);



// WEBPACK FOOTER //
// ./src/routes/Auth/auth.ducks.ts